import type {
    WorkOrderPriceActValue,
    WorkOrderPriceItem,
    WorkOrderPriceItemFilterInput,
    WorkOrderPriceSeasonValue,
    WorkOrderMtrInflationData,
    WorkOrderPriceData,
} from 'gql/graphql';
import { WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { formatNumberToRu } from 'utils';

import type { RowClassRules } from 'ag-grid-community';

type TValueGetterSeasonProps = {
    seasonId: number;
    costRounding: number;
    valueKey: keyof WorkOrderPriceSeasonValue;
    maxCostRoundingDigits?: number;
};

type TValueGetterActProps = {
    seasonId: number;
    actId: number;
    costRounding: number;
    valueKey: keyof WorkOrderPriceActValue;
};

export const getSeasonValueByKey =
    ({ valueKey, seasonId, costRounding, maxCostRoundingDigits }: TValueGetterSeasonProps) =>
    ({ data }: { data: WorkOrderPriceItem | null | undefined }) => {
        const value = data?.seasonValues?.find((season) => season.seasonId === seasonId);

        return formatNumberToRu(value?.[valueKey], costRounding, maxCostRoundingDigits) ?? '';
    };

export const getActValueByKey =
    ({ valueKey, seasonId, costRounding, actId }: TValueGetterActProps) =>
    ({ data }: { data: WorkOrderPriceItem | null | undefined }) => {
        const currentSeason = data?.seasonValues?.find((season) => season.seasonId === seasonId);

        const currentAct = currentSeason?.actValues?.find((act) => act.actId === actId);

        return formatNumberToRu(currentAct?.[valueKey], costRounding) ?? '';
    };

export const getUerWorkOrderRowClass = (workOrderStatus?: WorkOrderStatusEnum | undefined): RowClassRules => ({
    UER: ({ data }: { data: WorkOrderMtrInflationData | WorkOrderPriceItem }) =>
        data?.type === WorkOrderPriceTypeEnum.Uer,
    COEFFICIENT: ({ data }: { data: WorkOrderMtrInflationData | WorkOrderPriceData }) =>
        data?.type === WorkOrderPriceTypeEnum.Coefficient ||
        data?.type === WorkOrderPriceTypeEnum.ConstraintCoefficient ||
        data?.type === WorkOrderPriceTypeEnum.TechPartCoefficient ||
        data?.type === WorkOrderPriceTypeEnum.DismantlingCoefficient,
    MTR_CONTRACTOR_MATERIAL: ({ data }: { data: WorkOrderMtrInflationData | WorkOrderPriceData }) =>
        data?.type === WorkOrderPriceTypeEnum.MtrContractorMaterial,
    MTR_CUSTOMER_MATERIAL: ({ data }: { data: WorkOrderMtrInflationData | WorkOrderPriceData }) =>
        data?.type === WorkOrderPriceTypeEnum.MtrCustomerMaterial,
    MTR_CONTRACTOR_EQUIPMENT: ({ data }: { data: WorkOrderMtrInflationData | WorkOrderPriceData }) =>
        data?.type === WorkOrderPriceTypeEnum.MtrContractorEquipment,
    MTR_CUSTOMER_EQUIPMENT: ({ data }: { data: WorkOrderMtrInflationData | WorkOrderPriceData }) =>
        data?.type === WorkOrderPriceTypeEnum.MtrCustomerEquipment,
    alert: ({ data }: { data: WorkOrderPriceData }) =>
        !data?.isActKC2Price &&
        [
            WorkOrderPriceTypeEnum.Uer,
            WorkOrderPriceTypeEnum.Mtr,
            WorkOrderPriceTypeEnum.MtrCustomerMaterial,
            WorkOrderPriceTypeEnum.MtrCustomerEquipment,
            WorkOrderPriceTypeEnum.MtrContractorMaterial,
            WorkOrderPriceTypeEnum.MtrContractorEquipment,
        ].includes(data?.type) &&
        !!(data?.hasEmptySeasonVolume && workOrderStatus === WorkOrderStatusEnum.OnFormation),
    groupHeader: ({ data }: { data: WorkOrderPriceData }) => data?.type === WorkOrderPriceTypeEnum.RootObject,
    otherGroup: ({ data }: { data: WorkOrderPriceData }) => data?.type === WorkOrderPriceTypeEnum.OtherGroup,
    excludedClass: ({ data }: { data: WorkOrderPriceData }) => Boolean(data?.isExcludedPrice),
});

export const getWorkOrderWhereFromFilter = (fieldName: string, checkedValues: any[]): WorkOrderPriceItemFilterInput => {
    if (!checkedValues?.length) {
        if (fieldName === 'code') {
            return { or: undefined };
        }

        return {
            [fieldName]: undefined,
        };
    }

    if (fieldName === 'code') {
        return {
            or: checkedValues.map(({ uerCode, code, isUer }: { uerCode: string; code: string; isUer: boolean }) => ({
                and: [
                    { uerCode: { eq: uerCode } },
                    {
                        or: [{ code: { eq: isUer ? uerCode : code } }, { code: { eq: uerCode } }],
                    },
                ],
            })),
        };
    }

    return {
        [fieldName]: {
            in: checkedValues,
        },
    };
};

export const getWorkOrderVariables = (
    objectId: number | undefined,
    subObjectId: number | undefined,
    where?: WorkOrderPriceItemFilterInput,
) => {
    const objectIdWhere: WorkOrderPriceItemFilterInput[] = objectId ? [{ rootObjectId: { eq: objectId } }] : [];
    const subObjectIdWhere: WorkOrderPriceItemFilterInput[] = subObjectId
        ? [
              {
                  or: [{ objectId: { eq: subObjectId } }, { isRootObject: { eq: true } }],
              },
          ]
        : [];
    const filtersWhere = (
        Object.values(where ?? {}).filter(Boolean).length
            ? [
                  {
                      or: [where, { isRootObject: { eq: true } }],
                  },
              ]
            : []
    ) as WorkOrderPriceItemFilterInput[];

    const hasActive = objectIdWhere?.length || subObjectIdWhere?.length || filtersWhere?.length;

    if (hasActive) {
        return {
            where: {
                and: [...objectIdWhere, ...subObjectIdWhere, ...filtersWhere],
            },
        };
    }

    return {};
};

export const commentRules = {
    validate: (value: string | undefined) => {
        if (value && value?.length > 512) {
            return 'Не более 512 символов';
        }

        return true;
    },
};

export const getWorkOrderTableKey = ({
    workOrderId,
    constructionTypeId,
    objectId,
    subObjectId,
}: {
    workOrderId: number;
    constructionTypeId: number;
    objectId?: number;
    subObjectId?: number;
}) => `${workOrderId}_${constructionTypeId}_${objectId ?? ''}_${subObjectId ?? ''}`;
